import { User } from '@sendbird/chat';
import { useChannelSettingsContext } from '@sendbird/uikit-react/ChannelSettings/context';
import useLocalization from '@sendbird/uikit-react/hooks/useLocalization';
import UserListItem, {
  type UserListItemProps,
} from '@sendbird/uikit-react/ui/UserListItem';
import UserListItemMenu from '@sendbird/uikit-react/ui/UserListItemMenu';
import {
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useTranslate } from 'ra-core';
import { useToggleOperatorsStatus } from '../../../api/hooks/sendbird/useToggleOperatorsStatus';
import { AddOperatorsModal } from './AddOperatorsModal';
import { AllOperatorsModal } from './AllOperatorsModal';
import { ChannelSettingsMenuItem } from './ChannelSettingsMenuItem';
import { Button } from '../../../components/ui/button';

interface OperatorsListProps {
  renderUserListItem?: (
    props: UserListItemProps & { index: number }
  ) => ReactNode;
}

export const OperatorsList = ({
  renderUserListItem: RenderUserListItem = UserListItem,
}: OperatorsListProps): ReactElement => {
  const [operators, setOperators] = useState<User[]>([]);
  const [hasNext, setHasNext] = useState(false);
  const [showAllOperators, setShowAllOperators] = useState(false);
  const [showAddOperators, setShowAddOperators] = useState(false);
  const { channel, forceUpdateUI } = useChannelSettingsContext();
  const { stringSet } = useLocalization();
  const translate = useTranslate();

  const { mutate: toggleOperatorsStatus, isPending: isTogglePending } =
    useToggleOperatorsStatus({
      onSuccess: (_data, variables) => {
        if (!variables.isOperator) {
          // Unregister: remove from local list
          setOperators((prev) =>
            prev.filter((op) => !variables.userIds.includes(op.userId))
          );
        }
      },
    });

  const refreshList = useCallback(() => {
    const operatorListQuery = channel.createOperatorListQuery({
      // Short list of operators. If the user wants to see more, they can click the "Show all" button, which presents a modal
      limit: 10,
    });

    void operatorListQuery.next().then((nextOperators) => {
      setOperators(nextOperators);
      setHasNext(operatorListQuery.hasNext);
    });
  }, [channel]);

  useEffect(refreshList, [channel.url, refreshList]);

  const handleUnregisterOperator = (userId: string) => {
    toggleOperatorsStatus({
      channelUrl: channel.url,
      userIds: [userId],
      isOperator: false,
    });
  };

  return (
    <>
      {operators.map((operator, index) => (
        <RenderUserListItem
          key={operator.userId}
          index={index}
          user={operator}
          channel={channel}
          size="small"
          avatarSize="24px"
          renderListItemMenu={(props) => (
            <UserListItemMenu
              {...props}
              renderMenuItems={() => (
                <ChannelSettingsMenuItem
                  pending={isTogglePending}
                  onClick={() => handleUnregisterOperator(operator.userId)}
                >
                  {translate('chat.unregisterAsOperator')}
                </ChannelSettingsMenuItem>
              )}
            />
          )}
        />
      ))}

      {operators.length === 0 && (
        <p className="sendbird-channel-settings__empty-list">
          {stringSet.CHANNEL_SETTING__OPERATORS__TITLE_ALL}
        </p>
      )}

      <div className="sendbird-channel-settings-accordion__footer">
        {hasNext && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowAllOperators(true)}
          >
            {stringSet.CHANNEL_SETTING__OPERATORS__TITLE_ALL}
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowAddOperators(true)}
        >
          {translate('chat.addOperator')}
        </Button>
      </div>

      <AllOperatorsModal
        open={showAllOperators}
        onCancel={() => {
          setShowAllOperators(false);
          refreshList();
          forceUpdateUI();
        }}
      />

      <AddOperatorsModal
        open={showAddOperators}
        onCancel={() => {
          setShowAddOperators(false);
          refreshList();
          forceUpdateUI();
        }}
        onSubmit={() => {
          setShowAddOperators(false);
          refreshList();
          forceUpdateUI();
        }}
      />
    </>
  );
};
